// Opens a print-ready window with the current grid, formatted for PDF export.

import { STEP_ORDER, STEP_LABELS } from './deriveSession';
const escapeHtml = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmtItems = (items, sep) =>
  items.map((i) => `${escapeHtml(i.text)} (${i.votes > 0 ? '+' : ''}${i.votes})`).join(sep);


// The case's own narrative and what the room missed, appended when a guided
// case is running. Only revealed steps are included, so the export never
// carries a statement or an expert list the class has not seen yet.
const revealedSteps = (cf) =>
  STEP_ORDER.filter((s) => (s === 'cdm' ? !!cf.diagnosisRevealedAt : !!cf.completed?.[s]));

function caseSections(cf) {
  if (!cf?.active) return '';
  let out = '';
  const steps = revealedSteps(cf).filter((s) => (cf.problemRepresentation?.[s] || '').trim());
  if (steps.length) {
    out += '<h3>Problem representation</h3>';
    steps.forEach((s) => {
      out += `<p class="pr"><strong>After ${escapeHtml(STEP_LABELS[s])}:</strong> ${escapeHtml(cf.problemRepresentation[s])}</p>`;
    });
  }
  const gapScopes = revealedSteps(cf).filter((s) => cf.classGaps?.[s]?.items?.length);
  if (gapScopes.length) {
    out += '<h3>Class gaps</h3>';
    gapScopes.forEach((s) => {
      const g = cf.classGaps[s];
      out += `<table class="gaps"><thead><tr><th style="width: 30%">${escapeHtml(STEP_LABELS[s])}: missed by</th><th>Expert item</th><th style="width: 15%">Listed</th></tr></thead><tbody>`;
      g.items.forEach((item) => {
        out += `<tr><td class="missed">Missed by ${item.missed} of ${g.total}</td><td>${escapeHtml(item.text)}</td><td>${item.listed}</td></tr>`;
      });
      out += '</tbody></table>';
    });
  }
  return out;
}

export default function exportToPdf({ view, sessionId, cdmRows, diffRows, caseFlow = null }) {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    alert('Please allow pop-ups to export the PDF');
    return;
  }

  let content = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>CDM Grid Export - ${escapeHtml(sessionId)}</title>
      <style>
        body { font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; background: #F7F7FA; color: #1B1B2F; margin: 0; line-height: 1.45; }
        .container { max-width: 1200px; margin: 0 auto; background: #FFFFFF; padding: 32px; border: 1px solid #E4E4EC; border-radius: 8px; }
        h1 { color: #1B1B2F; margin-bottom: 4px; font-size: 22px; font-weight: 500; }
        h2 { color: #6B6B80; font-size: 15px; margin-bottom: 24px; font-weight: 400; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; border: 1px solid #E4E4EC; border-radius: 8px; overflow: hidden; }
        th { background: #F7F7FA; color: #6B6B80; padding: 10px 12px; text-align: left; font-weight: 500; font-size: 13px; text-transform: uppercase; letter-spacing: 0.02em; border-bottom: 1px solid #E4E4EC; }
        td { border-top: 1px solid #E4E4EC; padding: 12px; vertical-align: top; font-size: 15px; }
        .diagnosis { font-weight: 600; color: #1B1B2F; margin-bottom: 4px; font-size: 15px; }
        .rank { font-weight: 600; color: #5B4BC4; font-size: 22px; }
        .critical { border-left: 3px solid #D14343; }
        .critical .rank { color: #D14343; }
        .footer { text-align: center; margin-top: 32px; color: #6B6B80; font-size: 13px; padding-top: 16px; border-top: 1px solid #E4E4EC; }
        .evidence { color: #6B6B80; font-size: 13px; }
        h3 { color: #1B1B2F; font-size: 17px; font-weight: 500; margin: 24px 0 8px; }
        .pr { color: #1B1B2F; font-size: 15px; margin: 0 0 8px; }
        table.gaps th { background: #F7F7FA; }
        .missed { font-weight: 600; color: #1B1B2F; }
        @media print { body { padding: 0; background: white; } .container { border: none; padding: 16px; } }
      </style>
    </head>
    <body onload="window.print()">
      <div class="container">
      <h1>${view === 'cdm' ? 'CDM Grid' : 'Differential Diagnosis'}</h1>
      <h2>Session: ${escapeHtml(sessionId)} | Exported: ${new Date().toLocaleString()}</h2>
  `;

  if (view === 'cdm') {
    content += `<table><thead><tr><th style="width: 10%">Rank</th><th style="width: 25%">Diagnosis</th><th style="width: 32.5%">Supporting Evidence</th><th style="width: 32.5%">Opposing Evidence</th></tr></thead><tbody>`;
    cdmRows.forEach((row) => {
      content += `<tr ${row.isCritical ? 'class="critical"' : ''}>
        <td><span class="rank">${escapeHtml(row.displayOrder)}</span></td>
        <td><div class="diagnosis">${fmtItems(row.diagnoses, ', ') || 'None'}</div></td>
        <td><div class="evidence">${fmtItems(row.supporting, '; ') || 'None'}</div></td>
        <td><div class="evidence">${fmtItems(row.opposing, '; ') || 'None'}</div></td>
      </tr>`;
    });
    content += '</tbody></table>';
  } else {
    content += `<table><thead><tr><th style="width: 20%">Etiological Class</th><th style="width: 20%">Diseases</th><th style="width: 30%">Supporting Evidence</th><th style="width: 30%">Opposing Evidence</th></tr></thead><tbody>`;
    diffRows.forEach((row) => {
      content += `<tr>
        <td><strong>${fmtItems(row.etiologicalClass, ', ') || 'None'}</strong></td>
        <td><div class="evidence">${fmtItems(row.diseases, '; ') || 'None'}</div></td>
        <td><div class="evidence">${fmtItems(row.supporting, '; ') || 'None'}</div></td>
        <td><div class="evidence">${fmtItems(row.opposing, '; ') || 'None'}</div></td>
      </tr>`;
    });
    content += '</tbody></table>';
  }

  content += caseSections(caseFlow);
  content += `<div class="footer">Created by ZH (C2028)</div></div></body></html>`;
  const doc = printWindow.document;
  doc.open();
  doc.write(content);
  doc.close();
}
